import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { IDailyLoginsWidgetProps } from './daily-logins.models';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { OrganizationLevelType } from '../../../enums';
import { lineChartIncrements } from '../../../library/helpers/line-chart-increments';
import { formatDate, ILineChartDataPoint, LineChart, ThemeColours } from '@keplerco/core';
import { PagePath } from '../../../navigation/navigation.enums';
import { DateSearchParams, PermissionSearchParams } from '../../../models/overmind/search-params';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';

export function DailyLoginsWidget({ entity, path }: IDailyLoginsWidgetProps): JSX.Element {
  const actions = useAppActions();
  const { dateRange, companyVariables } = useAppState();

  const [loading, setLoading] = useState<boolean>(true);
  const [colourMap, setColourMap] = useState<Map<string, ThemeColours>>();
  const [data, setData] = useState<ILineChartDataPoint[]>([]);

  useEffect(() => {
    async function getData() {
      if (!entity) return;

      setLoading(true);

      const request: DateSearchParams & PermissionSearchParams = {
        startDate: path === PagePath.dashboard ? undefined : dateRange?.from?.toJSON(),
        endDate: path === PagePath.dashboard ? undefined : dateRange?.to?.toJSON(),
        organizationLevel: entity.organizationLevel,
        companySlug: companyVariables.slug,
        departmentSlug: undefined,
        teamSlug: undefined,
        learnerSlug: undefined,
      };

      if (entity.organizationLevel === OrganizationLevelType.Department) request.departmentSlug = entity.entity.slug;
      if (entity.organizationLevel === OrganizationLevelType.Team) request.teamSlug = entity.entity.slug;
      if (entity.organizationLevel === OrganizationLevelType.Learner) request.learnerSlug = entity.entity.slug;
      const response = await actions.analyticsGetDailyActivity(request);

      if (!response) {
        setColourMap(undefined);
        setData([]);
        setLoading(false);
        return;
      }

      setColourMap(new Map([['Logins', 'baby-blue']]));
      setData(response.data.map(item => ({ label: formatDate(new Date(item.dateTime)), value: [{ name: 'Logins', value: item.count, dataType: 'none' }] })));
      setLoading(false);
    }

    getData();
  }, [dateRange, companyVariables.slug, entity]);

  const increment = lineChartIncrements(data);

  return (
    <SkeletonOverlay isLoading={loading} width="100%">
      <div className="card">
        {!colourMap || !data.length ? (
          <EmptyState title="No daily logins" />
        ) : (
          <React.Fragment>
            <h3 style={{ marginBottom: 15 }}>Daily Logins</h3>

            <LineChart yIncrements={increment} colorMap={colourMap} data={data} height={400} width={1024} />
          </React.Fragment>
        )}
      </div>
    </SkeletonOverlay>
  );
}
